import { FC, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
    Box,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
    getSpecialChargesAction,
    saveSpecialChargesAction,
} from "../../../../redux/actions/specialChargeAction";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import {
    ISpecialChargeConfig,
    SpecialChargeType,
} from "../../../../typings/interfaces/responses/special-charge-response";

const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
        fontWeight: "bold",
        fontSize: "var(--fs-xs)",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: "var(--fs-base)",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

// Editable copy of a row: the amount as typed
type Row = Omit<ISpecialChargeConfig, "amount"> & { amount: string };

const rowKey = (row: Pick<ISpecialChargeConfig, "alterations" | "categoryId">) =>
    row.alterations ? "alterations" : String(row.categoryId);

// Admin > Special Charges: what a Special ticket costs on top of the default
// price list. Regular tickets pay the price list only.
export const SpecialChargesPage: FC = () => {
    const dispatch = useAppDispatch();
    const snackBar = useSnackbarContext();
    const { configs, configsLoading, saveLoading } = useAppSelector((state) => state.specialCharge);
    const [rows, setRows] = useState<Row[]>([]);

    useEffect(() => {
        dispatch(getSpecialChargesAction());
    }, []);

    useEffect(() => {
        setRows((configs ?? []).map((config) => ({ ...config, amount: String(config.amount) })));
    }, [configs]);

    const updateRow = (key: string, changes: Partial<Row>) =>
        setRows((prev) => prev.map((row) => (rowKey(row) === key ? { ...row, ...changes } : row)));

    const errorFor = (row: Row) => {
        const amount = Number(row.amount);
        if (row.amount.trim() === "" || Number.isNaN(amount)) return "Enter an amount";
        if (amount < 0) return "Can't be negative";
        if (row.chargeType === "PERCENTAGE" && amount > 100) return "Max 100%";
        return null;
    };
    const hasErrors = rows.some((row) => errorFor(row) !== null);

    const handleSave = async () => {
        if (hasErrors) return;
        try {
            await dispatch(
                saveSpecialChargesAction(rows.map((row) => ({ ...row, amount: Number(row.amount) })))
            ).unwrap();
            snackBar.showSnackbar("Special charges saved", "success");
        } catch (error) {
            snackBar.showSnackbar(
                typeof error === "string" ? error : "Failed to save special charges. Please try again.",
                "error"
            );
        }
    };

    return (
        <AdminLayout>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "3rem",
                    marginTop: "4.357rem",
                    paddingInline: "5rem",
                    width: "100%",
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {/* The admin header already shows the page title */}
                    <Typography sx={{ color: Colors.TEXT_GRAY_DARK, fontSize: "var(--fs-base)", mr: "2rem" }}>
                        Added to Special tickets on top of the default price list. Flat is per
                        garment (per alteration for Alterations); a percentage is of the item price.
                        Regular tickets pay the price list only.
                    </Typography>
                    <CustomButton
                        sx={{ width: "12rem", height: "3rem" }}
                        onClick={handleSave}
                        disabled={configsLoading || saveLoading || hasErrors || rows.length === 0}
                        data-testid="special-charges-save"
                    >
                        {saveLoading ? <CircularProgress size={27} color="inherit" /> : "Save"}
                    </CustomButton>
                </Box>

                <Table sx={{ width: "100%" }} aria-label="special charges">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Service</StyledTableCell>
                            <StyledTableCell>Charge</StyledTableCell>
                            <StyledTableCell>Amount</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {configsLoading && rows.length === 0 && (
                            <StyledTableRow>
                                <StyledTableCell colSpan={3} align="center">
                                    <CircularProgress size={27} color="inherit" />
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                        {rows.map((row) => {
                            const key = rowKey(row);
                            const error = errorFor(row);
                            return (
                                <StyledTableRow key={key} data-testid={`special-charge-row-${key}`}>
                                    <StyledTableCell sx={{ width: "40%" }}>{row.categoryName}</StyledTableCell>
                                    <StyledTableCell sx={{ width: "30%" }}>
                                        <Select
                                            size="small"
                                            value={row.chargeType}
                                            onChange={(e) =>
                                                updateRow(key, { chargeType: e.target.value as SpecialChargeType })
                                            }
                                            sx={{ minWidth: "14rem", fontSize: "var(--fs-base)" }}
                                            inputProps={{ "aria-label": `${row.categoryName} charge type` }}
                                        >
                                            <MenuItem value="FLAT">Flat ($)</MenuItem>
                                            <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                                        </Select>
                                    </StyledTableCell>
                                    <StyledTableCell sx={{ width: "30%" }}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={row.amount}
                                            onChange={(e) => updateRow(key, { amount: e.target.value })}
                                            error={error !== null}
                                            helperText={error ?? " "}
                                            inputProps={{
                                                min: 0,
                                                step: row.chargeType === "FLAT" ? 0.01 : 1,
                                                "aria-label": `${row.categoryName} amount`,
                                            }}
                                            InputProps={{
                                                startAdornment:
                                                    row.chargeType === "FLAT" ? (
                                                        <InputAdornment position="start">$</InputAdornment>
                                                    ) : undefined,
                                                endAdornment:
                                                    row.chargeType === "PERCENTAGE" ? (
                                                        <InputAdornment position="end">%</InputAdornment>
                                                    ) : undefined,
                                            }}
                                            sx={{ width: "14rem", "& input": { fontSize: "var(--fs-base)" } }}
                                        />
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        </AdminLayout>
    );
};
